import modalShiftPreviewUrl from '../../../shifts/modal-shift-preview.html';
import closeIconSvg from '../../../assets/svg/UI/close.svg';
import trashIconSvg from '../../../assets/svg/UI/trash.svg';
import backIconSvg from '../../../assets/svg/UI/back.svg';

import viewTaskInstanceUrl from './viewTaskInstance.html';

class ViewTaskInstanceCtrl {
  closeIconSvg = closeIconSvg;
  trashIconSvg = trashIconSvg;
  backIconSvg = backIconSvg;
  actionInProgress = false;
  shiftPreviewActive = false;
  canEdit = false;

  constructor(
    Authentication,
    AlertService,
    ConfirmService,
    ChooseService,
    EventCollection,
    PersonaCollection,
    Utils,
    ShiftTasksService,
    ShiftTasksActionsService,
    toTimeFilter,
    pluralizeFilter,
    sdk,
    $state,
    $uibModal
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      AlertService,
      ConfirmService,
      ChooseService,
      EventCollection,
      PersonaCollection,
      Utils,
      ShiftTasksService,
      ShiftTasksActionsService,
      toTimeFilter,
      pluralizeFilter,
      sdk,
      $state,
      $uibModal,
    });
    Utils.extendWithProps(sdk('constants'), this, [
      'TASK_INSTANCE_COMPLETED_STATUS',
      'TASK_INSTANCE_PENDING_STATUS',
    ]);

    this.canInteractWithTaskInstance = sdk('helpers/shiftTasks/canInteractWithTaskInstance');
    this.canInteractWithSubtasks = sdk('helpers/shiftTasks/canInteractWithSubtasks');
  }

  $onInit() {
    this.loadTaskInstanceDetails();
    this.setCanEditFlag();
    this.setCanDisplayEditBannerFlag();
  }

  async loadTaskInstanceDetails() {
    if (this.taskInstanceId) {
      try {
        this.taskInstance = await this.ShiftTasksService.loadTaskInstanceDetails(
          this.taskInstanceId
        );
        this.setCanEditFlag();
        this.setCanDisplayEditBannerFlag();
      } catch (e) {
        this.dismiss();
      }
    }
  }

  dismiss() {
    this.modal.dismiss();
  }

  close() {
    this.parentModal?.dismiss();
    this.modal.dismiss();
  }

  setCanEditFlag() {
    this.canEdit = this.hasRightToEdit() && !this.isPreview;
  }

  setCanDisplayEditBannerFlag() {
    this.canDisplayEditBanner = this.hasRightToEdit() && this.isPreview;
  }

  hasRightToEdit() {
    if (!this.taskInstance) {
      return false;
    }

    const { isManager, canActOnLocation } = this.Authentication.user;
    return isManager && canActOnLocation({ id: this.taskInstance.locationId });
  }

  editTaskInstance(event) {
    event?.preventDefault();
    this.parentModal?.close();

    const date = this.taskInstance.dtstart
      ? this.Utils.formatDate(new Date(this.taskInstance.dtstart))
      : '';
    this.modal.close();
    this.$state.go('shiftTasks.instances.edit', {
      taskInstanceId: this.taskInstance.id,
      date,
      isFromPreview: this.isPreview,
    });
  }

  hasSubtasks() {
    return this.taskInstance?.subtasks?.length;
  }

  hasAttachments() {
    return Boolean(this.taskInstance?.attachments?.length);
  }

  get isCompleted() {
    return this.taskInstance?.status === this.TASK_INSTANCE_COMPLETED_STATUS;
  }

  get canInteractWithTask() {
    if (!this.taskInstance) {
      return false;
    }
    return this.canInteractWithTaskInstance(this.taskInstance, this.Authentication.user);
  }

  get canInteractWithTaskInstanceSubtasks() {
    if (!this.taskInstance) {
      return false;
    }
    return this.canInteractWithSubtasks(this.taskInstance, this.Authentication.user);
  }

  get taskInstanceAssignee() {
    if (!this.taskInstance) {
      return '';
    }
    return this.ShiftTasksService.getAssigneeLabel(this.taskInstance);
  }

  get taskInstanceTime() {
    if (!this.taskInstance) {
      return '';
    }
    return this.ShiftTasksService.getTimeLabel(this.taskInstance);
  }

  get areDetailsEmpty() {
    return (
      this.isPreview && !this.taskInstance?.description && !this.taskInstance?.subtasks?.length
    );
  }

  async completeTask() {
    this.taskInstance = await this.ShiftTasksActionsService.completeTask(this.taskInstance);
  }

  async reopenTask() {
    await this.ShiftTasksActionsService.reopenTask(this.taskInstance);
  }

  async toggleSubtask(subtask) {
    if (subtask.status === this.TASK_INSTANCE_COMPLETED_STATUS) {
      this.reopenSubtask(subtask.id);
    } else {
      this.completeSubtask(subtask.id);
    }
  }

  async completeSubtask(subtaskId) {
    try {
      await this.ShiftTasksService.updateSubtaskStatus(
        this.taskInstanceId,
        subtaskId,
        this.TASK_INSTANCE_COMPLETED_STATUS
      );
      if (this.hasAllSubtasksCompleted(this.taskInstance)) {
        await this.completeTask();
      }
      this.actionInProgress = false;
    } catch (e) {
      this.actionInProgress = false;
    }
  }

  async reopenSubtask(subtaskId) {
    try {
      await this.ShiftTasksService.updateSubtaskStatus(
        this.taskInstanceId,
        subtaskId,
        this.TASK_INSTANCE_PENDING_STATUS
      );
      this.actionInProgress = false;
    } catch (e) {
      this.actionInProgress = false;
    }
  }

  deleteTaskInstance() {
    this.ConfirmService.ask(
      {
        question: `Are you sure you want to delete task: ${this.taskInstance.title}?`,
        title: 'Please confirm',
        confirm: 'Delete',
      },
      async () => {
        this.actionInProgress = true;
        try {
          await this.ShiftTasksService.remove(this.taskInstance);
          this.actionInProgress = false;
          this.dismiss();
        } catch (e) {
          this.actionInProgress = false;
        }
      }
    ).ignoreCancel();
  }

  hasAllSubtasksCompleted(taskInstance) {
    return taskInstance.subtasks?.every(
      (subtask) => subtask.status === this.TASK_INSTANCE_COMPLETED_STATUS
    );
  }

  async previewShift() {
    const shiftDetails = await this.EventCollection.getShiftDetails(this.taskInstance.eventId);
    this.shiftPreviewActive = true;

    this.$uibModal
      .open({
        templateUrl: modalShiftPreviewUrl,
        controllerProvider: async () =>
          (await import(/* webpackChunkName: "shifts" */ '../../../shifts/ModalShiftPreviewCtrl'))
            .default,
        controllerAs: 'ctrl',
        size: '620',
        resolve: {
          shift: () => shiftDetails,
        },
      })
      .result.then(({ date, shift }) => {
        this.modal.close();
        if (date && shift) {
          this.$state.go('shifts.details', { date, shift, id: shift.id, tab: 'fullschedule' });
        }
      })
      .catch(() => {})
      .finally(() => {
        this.shiftPreviewActive = false;
      });
  }
}

export default {
  templateUrl: viewTaskInstanceUrl,
  controller: ViewTaskInstanceCtrl,
  controllerAs: 'ctrl',
  bindings: {
    modal: '<',
    taskInstanceId: '<?',
    taskInstance: '<?',
    isPreview: '<?',
    parentModal: '<?',
  },
};
